/**
 * Header live du widget météo — toujours visible, identique en replié et déployé.
 *
 * La barre entière est un <button> cliquable qui toggle l'état replié. Le
 * chevron à droite sert d'INDICATEUR visuel d'état (pas de bouton dédié). Gain
 * UX : la cible tactile devient toute la largeur du widget, pas juste le coin
 * droit — utile en condition terrain (gants, mains froides).
 */

import { ChevronDown, Droplets, Wind, type LucideIcon } from "lucide-react";

import type { MeteoPoint, WeatherMeta } from "./types.js";

export function MeteoHeaderLive({
  point,
  meta,
  commune,
  replie,
  onToggleReplie,
  controlsId,
}: {
  point: MeteoPoint;
  meta: WeatherMeta;
  commune: string;
  replie: boolean;
  onToggleReplie: () => void;
  /** ID du contenu déployé contrôlé par ce bouton (pour aria-controls). */
  controlsId: string;
}) {
  const Icone = meta.icon;
  return (
    <button
      type="button"
      onClick={onToggleReplie}
      aria-expanded={!replie}
      aria-controls={controlsId}
      className="flex w-full flex-wrap items-center justify-between gap-3 rounded-lg bg-champagne-50/60 px-3 py-2 text-left transition-colors hover:bg-champagne-100/70 focus:outline-none focus:ring-2 focus:ring-champagne-300"
    >
      {/* Bloc 1 : icône (couleur métier) + T° + libellé code + commune inline */}
      <div className="flex items-center gap-3 leading-tight">
        <Icone aria-hidden className="h-6 w-6 shrink-0" stroke={meta.color} strokeWidth={1.6} />
        <div>
          <div className="mono tnum text-[18px] font-semibold text-champagne-900">
            {Math.round(point.temperature_c)}
            <span className="text-[13px] font-normal">°C</span>
          </div>
          <div className="text-[12px] text-champagne-700">
            {meta.label} · <strong className="font-medium">{commune}</strong>
          </div>
        </div>
      </div>

      {/* Bloc 2 : chips pluie / vent / humidité + chevron indicateur.
          Le chevron pivote 180° selon l'état (cohérent avec le focus :
          "vers le bas" = ouvrable, "vers le haut" = déjà ouvert). */}
      <div className="flex items-center gap-2">
        <div className="hidden items-center gap-2 md:flex">
          <ChipMesure
            icon={Droplets}
            valeur={`${point.precipitation_mm.toFixed(1)} mm`}
            couleur="rain"
          />
          <ChipMesure icon={Wind} valeur={`${Math.round(point.wind_kmh)} km/h`} couleur="ink" />
          <ChipMesure icon={Droplets} valeur={`${Math.round(point.humidity_pct)}%`} couleur="ink" />
        </div>
        <ChevronDown
          aria-hidden
          className={`h-4 w-4 text-champagne-700 transition-transform duration-300 ease-out ${replie ? "rotate-0" : "rotate-180"}`}
          strokeWidth={1.6}
        />
      </div>
    </button>
  );
}

function ChipMesure({
  icon: Icone,
  valeur,
  couleur,
}: {
  icon: LucideIcon;
  valeur: string;
  couleur: "rain" | "ink";
}) {
  const stroke = couleur === "rain" ? "#5B7A95" : "#7d6f55";
  return (
    <span className="mono tnum inline-flex items-center gap-1 rounded-full border border-champagne-200 bg-white px-2.5 py-1 text-[12px] text-champagne-900">
      <Icone aria-hidden className="h-3 w-3" stroke={stroke} strokeWidth={1.6} />
      <strong className="font-medium">{valeur}</strong>
    </span>
  );
}
